import type { LanguageDefinitionResponse, TemplateAutocompleteValue } from '@onefootprint/request-types/dashboard';
import type * as monaco from 'monaco-editor';

// Provides documentation on hover, e.g. for risk signals.
export function hoverProvider(
  monacoInstance: typeof monaco,
  languageSpec: LanguageDefinitionResponse,
  autocompleteItems: TemplateAutocompleteValue[],
): monaco.languages.HoverProvider {
  const languageName = languageSpec.languageName;

  return {
    provideHover: (model, position) => {
      const text = model.getValue();
      const lineNumber = position.lineNumber;
      const lineMaxColumn = model.getLineMaxColumn(lineNumber);

      const tokens = monacoInstance.editor.tokenize(text, languageName)[lineNumber - 1];

      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];

        // Only hover for identifiers (and regex-highlighted keywords).
        if (!token.type.startsWith('identifier') && !token.type.startsWith('keyword')) {
          continue;
        }

        const tokenStartColumn = token.offset + 1;
        let tokenEndColumn = lineMaxColumn;
        if (i + 1 < tokens.length) {
          tokenEndColumn = tokens[i + 1].offset + 1;
        }

        const tokenRange = new monacoInstance.Range(lineNumber, tokenStartColumn, lineNumber, tokenEndColumn);
        if (!tokenRange.containsPosition(position)) {
          continue;
        }

        const identifier = model.getValueInRange(tokenRange);

        const autocompleteToken = autocompleteItems.find(t => t.token === identifier);
        const namespace = identifier.split('.')[0];
        const featureName = identifier.split('.').slice(1).join('.');
        const featureSet = languageSpec.featureSets.find(fs => fs.kind === namespace);
        const feature = featureSet?.features.find(f => f.name === featureName);

        const sections: string[] = [];
        if (feature) sections.push(`**Description**: ${feature.description}`);
        if (featureSet) sections.push(`**Source:** ${featureSet.description}`);
        if (feature) sections.push(`**Type:** *${feature.kind}*`);
        if (autocompleteToken?.dataType) sections.push(`**Data Type:** \`${autocompleteToken.dataType}\``);
        if (feature?.kind === 'enum' && feature.values) {
          sections.push(`**Possible Values:**\n\`\`\`\n${feature.values.map(v => `"${v}"`).join(' | ')}\n\`\`\``);
        }

        if (sections.length === 0) return null;

        return {
          contents: [{ value: sections.join('\n\n') }],
          range: tokenRange,
        };
      }

      return null;
    },
  };
}
